import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Clock, CheckCircle2, XCircle } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { ISO_COUNTRY_OPTIONS } from "@/data/isoCountries";

const STATES = ["WB", "MH", "DL", "KA", "TN", "UP", "GJ", "RJ", "TG", "AP", "KL", "PB", "HR", "BR", "OR", "AS", "MP", "CH"];
const INCOME_SLABS = [
  { code: "31", label: "Below ₹1 Lakh" },
  { code: "32", label: "₹1 - 5 Lakh" },
  { code: "33", label: "₹5 - 10 Lakh" },
  { code: "34", label: "₹10 - 25 Lakh" },
  { code: "35", label: "₹25 Lakh - 1 Crore" },
  { code: "36", label: "Above ₹1 Crore" },
];

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.full_name || "", pan: "", dob: "", gender: "M", occupation_code: "01",
    tax_status: "01", holding_type: "SI", address1: "", city: "", state: "WB", pincode: "",
    country: "IND", bank_account_no: "", bank_ifsc: "", account_type: "SB",
    dividend_pay_mode: "01", comm_mode: "E",
    annual_income_slab: "32", politically_exposed_person: "N",
    fatca_country_of_birth: "IND", fatca_tax_residency_country: "IND", fatca_tax_id_number: "",
    holding_mode: "physical", depository_code: "CDSL", dp_id: "", dp_client_id: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const legacyUccStates = ["PENDING_REVIEW", "BSE_UCC_SUBMITTING", "PENDING_BSE_AUTHENTICATION", "PENDING_BSE_RECONCILIATION"];
  const kycStatus = legacyUccStates.includes(user?.kyc_status) ? "NOT_VERIFIED" : (user?.kyc_status || "NOT_VERIFIED");
  const onboardingStatus = user?.ucc_onboarding_status || (legacyUccStates.includes(user?.kyc_status) ? user.kyc_status : "NOT_STARTED");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [year, month, day] = form.dob.split("-");
      await clientApi.post("/onboarding/kyc", { ...form, dob: `${day}/${month}/${year}` });
      toast.success("UCC registration details saved for SMIFS review. KYC has not been completed yet.");
      await refreshUser();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (onboardingStatus !== "NOT_STARTED" && onboardingStatus !== "REJECTED") {
    const uccSubmitted = ["PENDING_BSE_AUTHENTICATION", "PENDING_BSE_RECONCILIATION", "ACTIVE", "SUBMITTED_TO_BSE"].includes(onboardingStatus);
    return (
      <ClientLayout>
        <div className="mx-auto max-w-lg py-16 text-center">
          {kycStatus === "APPROVED" ? (
            <>
              <CheckCircle2 className="mx-auto h-14 w-14 text-[var(--cta)]" />
              <h1 className="mt-4 font-heading text-2xl text-[var(--brand)]">BSE registration and KYC complete</h1>
                <p className="mt-2 text-[var(--ink-muted)]">BSE UCC <span className="font-mono-app font-semibold">{user.client_code}</span> · KYC verified</p>
              <Button className="mt-6 rounded-full bg-[var(--cta)]" onClick={() => navigate("/funds")} data-testid="go-explore-funds-button">Explore Funds</Button>
            </>
          ) : (
            <>
              <Clock className="mx-auto h-14 w-14 text-amber-500" />
              <h1 className="mt-4 font-heading text-2xl text-[var(--brand)]">BSE investor registration</h1>
              <div className="mt-5 space-y-3 text-left">
                <div className="rounded-xl border border-[var(--glass-border)] bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Step 1 · UCC registration</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2"><StatusBadge status={onboardingStatus} /><span className="text-sm">Client code <span className="font-mono-app">{user.client_code}</span></span></div>
                  <p className="mt-2 text-xs text-[var(--ink-muted)]">{onboardingStatus === "PENDING_REVIEW" ? "SMIFS is reviewing the registration details before sending add_ucc to BSE." : "The UCC has been sent to BSE. Its status does not prove KYC completion."}</p>
                </div>
                <div className="rounded-xl border border-[var(--glass-border)] bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Step 2 · KYC verification</div>
                  <div className="mt-1"><StatusBadge status={kycStatus} /></div>
                  <p className="mt-2 text-xs text-[var(--ink-muted)]">KYC is a separate BSE-hosted journey. UCC registration alone is not KYC.</p>
                </div>
              </div>
              {uccSubmitted && <div className="mt-4">
                <Button variant="outline" className="rounded-full" data-testid="bse-kyc-link-button"
                  onClick={async () => {
                    try {
                      const { data } = await clientApi.get("/onboarding/kyc-link");
                      if (data.kyc_link) window.open(data.kyc_link, "_blank");
                      else toast.error(data.message || "BSE did not return a KYC link");
                    } catch (err) { toast.error(getApiErrorMessage(err)); }
                  }}>
                  Generate BSE KYC link
                </Button>
              </div>}
            </>
          )}
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-[var(--cta)]" />
          <h1 className="font-heading text-2xl text-[var(--brand)]">Step 1 · BSE investor registration (UCC)</h1>
        </div>
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950"><b>This is UCC registration, not KYC.</b> SMIFS reviews these investor, bank and holding details and then submits <span className="font-mono">add_ucc</span> to BSE. If BSE requires KYC, the separate BSE-hosted KYC step appears afterward.</div>

        <Card className="glass-card border-0">
          <CardHeader><CardTitle className="font-heading text-lg text-[var(--brand)]">Personal & Bank Details</CardTitle></CardHeader>
          <CardContent>
            <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Full name</Label>
                <Input required value={form.first_name} onChange={update("first_name")} data-testid="kyc-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>PAN</Label>
                <Input required maxLength={10} placeholder="ABCPD1234E" value={form.pan} onChange={(e) => update("pan")(e.target.value.toUpperCase())} data-testid="kyc-pan-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Date of birth</Label>
                <Input required type="date" value={form.dob} onChange={update("dob")} data-testid="kyc-dob-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Address</Label>
                <Input required value={form.address1} onChange={update("address1")} data-testid="kyc-address-input" />
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input required value={form.city} onChange={update("city")} data-testid="kyc-city-input" />
              </div>
              <div className="space-y-1.5">
                <Label>State</Label>
                <Select value={form.state} onValueChange={update("state")}>
                  <SelectTrigger data-testid="kyc-state-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Investor tax category</Label>
                <Select value={form.tax_status} onValueChange={update("tax_status")}>
                  <SelectTrigger data-testid="kyc-tax-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="01">01 — Resident individual</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Holding nature</Label>
                <Select value={form.holding_type} onValueChange={update("holding_type")}>
                  <SelectTrigger data-testid="kyc-holding-nature-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="SI">SI — Single holder</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Pincode</Label>
                <Input required maxLength={6} value={form.pincode} onChange={update("pincode")} data-testid="kyc-pincode-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={update("gender")}>
                  <SelectTrigger data-testid="kyc-gender-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Male</SelectItem>
                    <SelectItem value="F">Female</SelectItem>
                    <SelectItem value="O">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Bank account number</Label>
                <Input required value={form.bank_account_no} onChange={update("bank_account_no")} data-testid="kyc-bank-account-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Bank IFSC</Label>
                <Input required maxLength={11} placeholder="HDFC0001234" value={form.bank_ifsc} onChange={(e) => update("bank_ifsc")(e.target.value.toUpperCase())} data-testid="kyc-ifsc-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Account type</Label>
                <Select value={form.account_type} onValueChange={update("account_type")}>
                  <SelectTrigger data-testid="kyc-account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SB">Savings</SelectItem>
                    <SelectItem value="CB">Current</SelectItem>
                    <SelectItem value="NE">NRE</SelectItem>
                    <SelectItem value="NO">NRO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Mutual fund holding mode</Label>
                <Select value={form.holding_mode} onValueChange={update("holding_mode")}>
                  <SelectTrigger data-testid="kyc-holding-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">Statement of Account (Physical)</SelectItem>
                    <SelectItem value="demat">Demat only</SelectItem>
                    <SelectItem value="both">Both physical and demat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.holding_mode !== "physical" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Depository</Label>
                    <Select value={form.depository_code} onValueChange={update("depository_code")}>
                      <SelectTrigger data-testid="kyc-depository-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CDSL">CDSL</SelectItem>
                        <SelectItem value="NSDL">NSDL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>DP ID</Label>
                    <Input required value={form.dp_id} onChange={update("dp_id")} data-testid="kyc-dp-id-input" />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Demat client ID</Label>
                    <Input required value={form.dp_client_id} onChange={update("dp_client_id")} data-testid="kyc-dp-client-id-input" />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label>Occupation</Label>
                <Select value={form.occupation_code} onValueChange={update("occupation_code")}>
                  <SelectTrigger data-testid="kyc-occupation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="01">Business</SelectItem>
                    <SelectItem value="02">Services (Private)</SelectItem>
                    <SelectItem value="03">Services (Government)</SelectItem>
                    <SelectItem value="04">Professional</SelectItem>
                    <SelectItem value="05">Agriculturist</SelectItem>
                    <SelectItem value="06">Retired</SelectItem>
                    <SelectItem value="07">Housewife</SelectItem>
                    <SelectItem value="08">Student</SelectItem>
                    <SelectItem value="99">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Gross annual income</Label>
                <Select value={form.annual_income_slab} onValueChange={update("annual_income_slab")}>
                  <SelectTrigger data-testid="kyc-income-slab-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{INCOME_SLABS.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--surface-2)] p-4">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--brand)]">FATCA / Tax Residency Declaration</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Are you a politically exposed person (PEP)?</Label>
                      <Select value={form.politically_exposed_person} onValueChange={update("politically_exposed_person")}>
                        <SelectTrigger data-testid="kyc-pep-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="N">No</SelectItem>
                          <SelectItem value="Y">Yes</SelectItem>
                          <SelectItem value="R">Relative of a PEP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Country of birth</Label>
                      <Select value={form.fatca_country_of_birth} onValueChange={update("fatca_country_of_birth")}>
                        <SelectTrigger data-testid="kyc-fatca-birth-country-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{ISO_COUNTRY_OPTIONS.map((country) => <SelectItem key={country.value} value={country.value}>{country.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Tax residency country</Label>
                      <Select value={form.fatca_tax_residency_country} onValueChange={update("fatca_tax_residency_country")}>
                        <SelectTrigger data-testid="kyc-fatca-tax-residency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{ISO_COUNTRY_OPTIONS.map((country) => <SelectItem key={country.value} value={country.value}>{country.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {form.fatca_tax_residency_country !== "IND" && (
                      <div className="space-y-1.5">
                        <Label>Tax Identification Number</Label>
                        <Input required value={form.fatca_tax_id_number} onChange={update("fatca_tax_id_number")} data-testid="kyc-fatca-tax-id-input" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="kyc-submit-button">
                  {loading ? "Saving..." : "Submit UCC details for review"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
